import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle,
  LocalShipping,
  Analytics,
  Inventory,
  RouteOutlined,
  TrendingDown,
  Speed,
  Assessment,
  ArrowForward,
  Timeline,
  Warehouse,
  Map,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LogisticsPage: React.FC = () => {
  const navigate = useNavigate();

  const solutions = [
    {
      title: 'Supply Chain Optimization',
      icon: <Timeline />,
      description: 'End-to-end supply chain analysis and optimization to reduce costs and improve efficiency.',
      benefits: [
        '30% reduction in supply chain costs',
        'Real-time visibility across operations',
        'Predictive demand forecasting',
        'Supplier performance management',
      ],
    },
    {
      title: 'Inventory Management',
      icon: <Inventory />,
      description: 'Advanced inventory optimization strategies to minimize carrying costs while ensuring availability.',
      benefits: [
        'Reduce inventory holding by 25%',
        'Automated reorder points',
        'ABC analysis implementation',
        'Safety stock optimization',
      ],
    },
    {
      title: 'Distribution & Route Planning',
      icon: <RouteOutlined />,
      description: 'Intelligent route optimization and distribution network design for maximum efficiency.',
      benefits: [
        '20% reduction in delivery costs',
        'Dynamic route optimization',
        'Fleet utilization improvement',
        'Last-mile delivery solutions',
      ],
    },
    {
      title: 'Warehouse Operations',
      icon: <Warehouse />,
      description: 'Streamline warehouse operations with layout optimization and process improvements.',
      benefits: [
        '40% improvement in picking efficiency',
        'Optimized warehouse layout',
        'Cross-docking strategies',
        'Labor management systems',
      ],
    },
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Current State Analysis',
      description: 'Comprehensive assessment of existing logistics operations and pain points.',
      duration: '2-3 weeks',
    },
    {
      step: 2,
      title: 'Opportunity Identification',
      description: 'Data-driven analysis to identify cost reduction and efficiency opportunities.',
      duration: '1-2 weeks',
    },
    {
      step: 3,
      title: 'Solution Design',
      description: 'Custom strategy development with detailed implementation roadmap.',
      duration: '2-3 weeks',
    },
    {
      step: 4,
      title: 'Implementation',
      description: 'Phased rollout with continuous monitoring and adjustment.',
      duration: '8-12 weeks',
    },
    {
      step: 5,
      title: 'Optimization',
      description: 'Ongoing refinement and performance improvement.',
      duration: 'Continuous',
    },
  ];

  const metrics = [
    { metric: 'Cost Reduction', value: 30, unit: '%', description: 'Average supply chain cost savings' },
    { metric: 'Delivery Time', value: 25, unit: '%', description: 'Improvement in delivery speed' },
    { metric: 'Inventory Turns', value: 40, unit: '%', description: 'Increase in inventory turnover' },
    { metric: 'Order Accuracy', value: 99.5, unit: '%', description: 'Order fulfillment accuracy' },
  ];

  const industries = [
    'Government Agencies',
    'Healthcare & Pharmaceuticals',
    'Retail & E-commerce',
    'Manufacturing',
    'Food & Beverage',
    'Aerospace & Defense',
  ];

  const caseStudy = {
    client: 'Federal Distribution Agency',
    challenge: 'Inefficient distribution network resulting in high costs and delayed deliveries',
    solution: 'Implemented route optimization and warehouse consolidation strategy',
    results: [
      '35% reduction in transportation costs',
      '50% improvement in on-time delivery',
      '20% reduction in warehouse operational costs',
      'ROI achieved in 8 months',
    ],
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Logistics Consulting Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.95 }}>
                Transform your supply chain with data-driven optimization strategies 
                that reduce costs and improve operational efficiency.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    }
                  }}
                  onClick={() => navigate('/contact')}
                  endIcon={<ArrowForward />}
                >
                  Get Free Analysis
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    color: 'white', 
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  Download ROI Calculator
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <LocalShipping sx={{ fontSize: 200, opacity: 0.3 }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Key Metrics */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Proven Results
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {metrics.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.metric}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h2" color="primary" fontWeight={700}>
                    {item.value}{item.unit}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {item.metric}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Solutions */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Comprehensive Logistics Solutions
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {solutions.map((solution) => (
              <Grid item xs={12} md={6} key={solution.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: 'primary.main', mr: 2 }}>
                        {solution.icon}
                      </Box>
                      <Typography variant="h5" fontWeight={600}>
                        {solution.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" paragraph>
                      {solution.description}
                    </Typography>
                    <List>
                      {solution.benefits.map((benefit) => (
                        <ListItem key={benefit} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText primary={benefit} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Our Consulting Process
          </Typography>
          <Box sx={{ mt: 6 }}>
            {processSteps.map((step, index) => (
              <Box key={step.step} sx={{ mb: 4 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                          fontWeight: 700,
                        }}
                      >
                        {step.step}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={10}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6" fontWeight={600}>
                            {step.title}
                          </Typography>
                          <Chip label={step.duration} size="small" color="primary" />
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                          {step.description}
                        </Typography>
                        {index < processSteps.length - 1 && (
                          <LinearProgress 
                            variant="determinate" 
                            value={(index + 1) * 20} 
                            sx={{ mt: 2 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Case Study */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h2" gutterBottom textAlign="center">
            Success Story
          </Typography>
          <Card sx={{ mt: 4, bgcolor: 'primary.main', color: 'white' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom>
                {caseStudy.client}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
                Challenge:
              </Typography>
              <Typography variant="body1" paragraph>
                {caseStudy.challenge}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
                Solution:
              </Typography>
              <Typography variant="body1" paragraph>
                {caseStudy.solution}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
                Results:
              </Typography>
              <List>
                {caseStudy.results.map((result) => (
                  <ListItem key={result} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <TrendingDown sx={{ fontSize: 20, color: 'secondary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={result} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Industries */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Industries We Serve
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
            {industries.map((industry) => (
              <Paper 
                key={industry}
                sx={{ 
                  p: 2, 
                  px: 3,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Map sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  {industry}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom>
            Optimize Your Supply Chain Today
          </Typography>
          <Typography variant="h6" sx={{ mb: 4 }}>
            Get a free logistics assessment and discover your cost-saving opportunities.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              color="secondary"
              onClick={() => navigate('/contact')}
            >
              Schedule Free Assessment
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              sx={{ 
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              Download Case Studies
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LogisticsPage;